export type OnError = (error: Error) => void;
export type OnMessage = (data: { data: string }, opts: { isFirstMessage: boolean }) => void;
export type OnOpen = () => void;

class WebSocketRequest {
  hasForcedClose: boolean;
  hasSignaledClose: boolean;
  hasSignaledError: boolean;
  hasSignaledMessage: boolean;
  hasSignaledOpen: boolean;
  _onError?: OnError;
  _onMessage?: OnMessage;
  _onOpen?: OnOpen;
  url: string;
  socket: WebSocket;

  constructor({
    onError,
    onMessage,
    onOpen,
    url,
  }: {
    onError?: OnError;
    onMessage?: OnMessage;
    onOpen?: OnOpen;
    url: string;
  }) {
    this.hasForcedClose = false;
    this.hasSignaledClose = false;
    this.hasSignaledError = false;
    this.hasSignaledMessage = false;
    this.hasSignaledOpen = false;
    this._onError = onError;
    this._onMessage = onMessage;
    this._onOpen = onOpen;
    this.url = url;

    this.socket = new WebSocket(url);
    this.socket.addEventListener('close', (event) => this.onClose(event));
    this.socket.addEventListener('error', (event) => this.onError(event));
    this.socket.addEventListener('message', (event) => this.onMessage(event));
    this.socket.addEventListener('open', () => this.onOpen());
  }

  onOpen() {
    if (this.hasSignaledOpen) return;
    this.hasSignaledOpen = true;

    if (this._onOpen) this._onOpen();
  }

  onError(_: Event) {
    if (this.hasSignaledError) return;
    this.hasSignaledError = true;
  }

  onClose(event: CloseEvent) {
    if (this.hasSignaledClose) return;
    this.hasSignaledClose = true;

    const code = event ? event.code : undefined;
    const reason = event ? event.reason : undefined;
    let err: Error & { code?: number; reason?: string; hasSignaledError?: boolean };
    let reasonAndCode = '';
    if (code) reasonAndCode += `code: ${code}`;
    if (reason) reasonAndCode += `${code ? ', ' : ''}reason: ${reason}`;

    if (this.hasSignaledMessage) {
      err = new Error(`WebSocket connection closed${reasonAndCode ? `: ${reasonAndCode}` : ''}`);
    } else {
      err = new Error(
        `Could not establish WebSocket connection${reasonAndCode ? `: ${reasonAndCode}` : ''}`
      );
    }

    if (code) err.code = code;
    if (reason) err.reason = reason;
    err.hasSignaledError = this.hasSignaledError;

    if (this._onError) this._onError(err);
  }

  onMessage(event: { data: string }) {
    const isFirstMessage = !this.hasSignaledMessage;
    if (!this.hasSignaledMessage) this.hasSignaledMessage = true;

    if (this._onMessage) this._onMessage(event, { isFirstMessage });
  }

  close(code?: number, reason?: string) {
    if (this.hasSignaledClose || this.hasForcedClose) return;
    this.hasForcedClose = true;
    return this.socket.close(code, reason);
  }

  send(message: string) {
    return this.socket.send(message);
  }
}

export default WebSocketRequest;
